import React from "react";
import { IconTrash } from "@tabler/icons-react";
import {
  ColumnSuggestionsOUT,
  LogicalOperatorENUM,
  PropertyBrowserTreeNode,
  PropertyTypeChoices,
} from "api-client";
import DebouncedInput from "components/DebounceInput";
import DropdownBrowser from "components/DropdownBrowser";
import DropdownWithSearch from "components/DropdownWithSearch";
import SimpleSelect from "components/SimpleSelect";
import { BOOLEAN_OPTIONS } from "utils";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { FilterRule } from ".";
import ComboboxSelect from "components/ComboboxSelect";
import {
  LOGICAL_OPERATORS,
  PROPERTY_RULE_OPERATORS,
  PROPERTY_RULE_OPERATORS_MULTI_SELECT,
  PROPERTY_RULE_OPERATORS_NUMERIC,
  PROPERTY_RULE_OPERATORS_SINGLE_SELECT,
  PROPERTY_RULE_OPERATORS_TEXTUAL,
} from "features/DynamicTable/consts";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

interface BooleanOptionProps {
  id: string;
  name: string;
  value: null | boolean;
  value_count: number;
  tooltip?: string;
}

function operatorsByPropertyType(
  property_type: PropertyTypeChoices | undefined
): { name: string; name_de: string; id: string }[] {
  let operators = PROPERTY_RULE_OPERATORS;

  if (property_type == PropertyTypeChoices.Number) {
    operators = PROPERTY_RULE_OPERATORS_NUMERIC;
  } else if (property_type == PropertyTypeChoices.Boolean) {
    operators = [PROPERTY_RULE_OPERATORS[0]];
  } else if (property_type == PropertyTypeChoices.Text) {
    operators = PROPERTY_RULE_OPERATORS_TEXTUAL;
  } else if (property_type == PropertyTypeChoices.Multiselect) {
    operators = PROPERTY_RULE_OPERATORS_MULTI_SELECT;
  } else if (property_type == PropertyTypeChoices.Select) {
    operators = PROPERTY_RULE_OPERATORS_SINGLE_SELECT;
  }

  return operators;
}

const FilterRow = (props: {
  rule: FilterRule;
  index: number;
  onChangeProperty: (value: PropertyBrowserTreeNode[], index: number) => void;
  items: PropertyBrowserTreeNode[];
  wrapperID: string;
  scrollPos: number;
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  const shouldHideValueField = ["IS_EMPTY", "IS_NOT_EMPTY"].includes(
    props.rule.operator
  );
  const availableOperators = operatorsByPropertyType(
    props.rule.property?.property_type
  );

  const isPropertyTypeBoolean = () => {
    return props.rule.property?.property_type == PropertyTypeChoices.Boolean;
  };

  const getBooleanSuggestedValues = () => {
    const values = BOOLEAN_OPTIONS.map((item) => ({
      id: item.name,
      name: isEng ? item.name : item.name_de,
      value: item.value as null | boolean,
      value_count:
        props.rule.suggestedValues?.find((rec) => {
          let recValue = rec.value;
          if (recValue === "True") recValue = true;
          if (recValue === "False") recValue = false;
          return recValue === item.value;
        })?.value_count ?? 0,
    }));
    return values;
  };

  const onChangeTerm = (value: string | number | undefined, index: number) => {
    const rules = [
      ...dynamicTableStore.filterStore.selectedFilterByItems.rules,
    ];
    rules[index].value = String(value);
    dynamicTableStore.filterStore.updateFilterRules(rules);
  };

  const onChangeLogicalOperator = (value: string | undefined) => {
    dynamicTableStore.filterStore.setSelectedFilterByItems({
      ...dynamicTableStore.filterStore.selectedFilterByItems,
      logical_operator: value as LogicalOperatorENUM,
    });
  };

  const onChangeOperator = (value: string | undefined, index: number) => {
    const rules = [
      ...dynamicTableStore.filterStore.selectedFilterByItems.rules,
    ];
    rules[index].operator = String(value);
    dynamicTableStore.filterStore.updateFilterRules(rules);
  };

  const removeCurrentRow = (index: number) => {
    if (
      index == 0 &&
      dynamicTableStore.filterStore.selectedFilterByItems.rules.length <= 1
    ) {
      dynamicTableStore.filterStore.removeAllFilteringRules();
      return;
    }

    const rules = [
      ...dynamicTableStore.filterStore.selectedFilterByItems.rules,
    ];
    rules.splice(index, 1);
    dynamicTableStore.filterStore.updateFilterRules(rules);
  };

  const propertyValueSelector = () => {
    if (isPropertyTypeBoolean()) {
      const options = getBooleanSuggestedValues();
      const selectedOption = options.find(
        (rec) => String(rec.value) === props.rule.value
      );
      return (
        <ComboboxSelect<BooleanOptionProps>
          items={options}
          displayKey="name"
          detailKey="value_count"
          selectedOption={selectedOption}
          height="middle"
          handleChange={(selected) => {
            onChangeTerm(String(selected.value), props.index);
            dynamicTableStore.filterStore.setOpenFilter(false);
          }}
          placeholder={t("commons.selectValue")}
          containerClassName="max-w-[160px] min-w-[160px] bg-white rounded-md"
        />
      );
    }

    if (props.rule.suggestedValues?.length && !shouldHideValueField) {
      return (
        <DropdownWithSearch<ColumnSuggestionsOUT>
          items={props.rule.suggestedValues}
          displayKey="value"
          detailKey="value_count"
          value={props.rule.value}
          noMargin
          height="middle"
          handleSelect={(selected) =>
            onChangeTerm(String(selected.value), props.index)
          }
          labelWithSearch
          placeholder={t("mappingEditor.enterValue")}
          containerClassName="max-w-[160px] min-w-[160px]"
          onChangeSearchTerm={(value) =>
            value && onChangeTerm(value, props.index)
          }
          handleRemoveValue={() => onChangeTerm("", props.index)}
        />
      );
    } else {
      return (
        <DebouncedInput
          containerClassName="mt-0 max-w-[160px] min-w-[160px]"
          height="middle"
          onChange={(value) => onChangeTerm(value, props.index)}
          value={props.rule.value}
          hidden={shouldHideValueField}
          icon={<></>}
          placeholder={t("mappingEditor.enterValue")}
          className="placeholder-gray-500"
        />
      );
    }
  };

  return (
    <div className="flex items-center py-2 px-3 gap-2 min-w-[595px] hover:bg-indigo-100 transition group/filterItem">
      {props.index === 0 ? (
        <span className="min-w-[85px] text-right">
          {t("mappingEditor.when")}
        </span>
      ) : (
        <SimpleSelect
          options={LOGICAL_OPERATORS}
          noMargin
          className="!mb-0 !mt-0"
          current={
            dynamicTableStore.filterStore.selectedFilterByItems.logical_operator
          }
          containerClassName="min-w-[85px]"
          onChange={(value) => onChangeLogicalOperator(value)}
        />
      )}
      <DropdownBrowser<PropertyBrowserTreeNode>
        displayKey={isEng ? "name" : "name_de"}
        detailKey={isEng ? "classification" : "classification_de"}
        handleSelect={(value) => props.onChangeProperty(value, props.index)}
        name="filter_by"
        items={props.items}
        suggestedItems={dynamicTableStore.visibleColumnProperties}
        checkedItems={props.rule.property ? [props.rule.property] : []}
        placeholder={t("mappingEditor.findProperty")}
        optionsClassName="!max-w-fit min-w-[246px]"
        containerClassName="ml-1 relative bg-white"
        singleSelect
        hasSelectInput
        selectInputClassName="max-w-[134px] w-[134px]"
        fixedPos
        wrapperID={props.wrapperID}
        leftOffset={110}
        parentScrollTop={props.scrollPos}
        scrollAreaHeight={420}
      />
      {isPropertyTypeBoolean() ? (
        isEng ? (
          availableOperators[0].name
        ) : (
          availableOperators[0].name_de
        )
      ) : (
        <SimpleSelect
          options={availableOperators}
          noMargin
          className="!mb-0 !mt-0 min-w-[120px] max-w-[120px]"
          onChange={(value) => onChangeOperator(value, props.index)}
          current={props.rule.operator}
        />
      )}
      {propertyValueSelector()}
      <IconTrash
        className="cursor-pointer ml-2 transition group-hover/filterItem:text-indigo-700 min-w-[20px]"
        size={20}
        onClick={() => removeCurrentRow(props.index)}
      />
    </div>
  );
};

export default observer(FilterRow);
